import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 097/097R Worker Profile API. Every
// query param is optional - visibility scoping (Agent/PLC/SuperAgent/
// SuperAdmin) always happens on the backend from the caller's own session,
// never from a value this file sends. This page is read-only: there is no
// createWorkerProfile export for THIS page's own list at all - a Worker
// Profile is only ever created from an approved e-KYC review, which is what
// createWorkerProfileFromEkycReview at the bottom of this file does (P148).

const LIST_PATH = "/backoffice/worker-profiles";

// Exactly the param names the P098B contract whitelists - anything else
// passed to listWorkerProfiles is silently dropped rather than forwarded, so
// a typo'd key (or a future filter added to the UI without updating this
// whitelist) can never accidentally reach the backend as an unfiltered
// pass-through. Deliberately no dateFromUtc/dateToUtc here - unlike
// ekycReviewsApi.js, the P098B contract does not whitelist a date-range
// filter for this list.
const LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "workerStatus",
  "complianceStatus",
  "nationality",
  "preferredLanguageCode",
  "agentId",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "search",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`). */
function buildListQuery(params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of LIST_PARAM_KEYS) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listWorkerProfiles(params = {}) {
  return apiClient.get(`${LIST_PATH}${buildListQuery(params)}`);
}

// Detail fetch requires a real, non-empty workerId - this file never issues
// a request for a missing/blank id (the caller, useWorkerProfilesList's
// sibling in WorkerProfilesPage.jsx, only calls this once a row/card is
// actually clicked).
export function getWorkerProfile(workerId) {
  if (!workerId) {
    throw new Error("getWorkerProfile requires a workerId");
  }
  return apiClient.get(`${LIST_PATH}/${encodeURIComponent(workerId)}`);
}

/**
 * P148: creates the dbo.WmxWorkers row for an e-KYC review that has been
 * approved, via the real, already-existing
 * POST /api/backoffice/ekyc-reviews/{reviewId}/worker-profile route.
 *
 * This is the step that was previously a permanently-disabled button on the
 * e-KYC queue: the endpoint existed the whole time, nothing called it, and the
 * lifecycle dead-ended at "approved" with no way forward. Scope and permission
 * are resolved on the backend from the caller's own session, and the service
 * refuses any review that is not actually approved - this wrapper adds no rules
 * of its own.
 */
export function createWorkerProfileFromEkycReview(reviewId) {
  if (!reviewId) {
    throw new Error("createWorkerProfileFromEkycReview requires a reviewId");
  }
  return apiClient.post(`/backoffice/ekyc-reviews/${encodeURIComponent(reviewId)}/worker-profile`);
}
